import i18n from 'lib/i18next';
import Layout from 'components/Layout';
import { useTranslation } from 'react-i18next';
import { fetchGuidesFeaturedArticles, fetchArticles, fetchAllComparisons } from 'lib/AutolistAPI';
import { trackArticleClick } from 'lib/Tracking';
import DeviceScreen, { SMALL } from 'lib/DeviceScreen';
import { screen, colors } from 'components/styles/variables';
import FeaturedArticleSummaries from 'components/FeaturedArticleSummaries';
import ArticleSummaries, {
  ArticleLink,
  ComparisonLink,
  makeArticleTitleLinks,
} from 'components/ArticleSummaries';
import GuidesNavbar from 'components/GuidesNavbar';
import guidesAndReviewsHeroPng from 'public/static/images/guides-and-reviews-hero.png';
import NavbarPosition from 'lib/GuidesNavbarPosition';
import openGraphImage from 'public/static/images/guides_image.png';
import Head from 'next/head';
import { CONTENT_URL } from 'lib/ContentGroups';
import { fetchAllSlideshows } from 'lib/Repositories/httpSlideshowRepository';
import SlideshowInterface from 'types/Slideshow';
import ArticleSummariesPresenter from 'lib/Presenters/ArticleSummariesPresenter';
import { useState, useEffect } from 'react';
import { useWindowScroll } from 'react-use';
import { useContentGroups } from 'lib/hooks/useGoogleAnalytics';
import StructuredDataComponent from 'components/StructuredData';
import breadcrumbStructuredData from 'lib/StructuredData/breadcrumbStructuredData';
import useAdSetup from 'lib/hooks/useAdSetup';
import { GetServerSideProps } from 'next';
import captureException from 'lib/captureException';
import { AgeType, PageType, Zone } from 'lib/CarGurusAds/TaxonomySchema';
import { enableContentCaching } from 'lib/cacheControl';
import CarGurusAdUnit from 'lib/CarGurusAds/AdUnit';
import isIndexable from 'lib/isIndexable';

const ArticlesTitleLinks = makeArticleTitleLinks(ArticleLink);
const ComparisonTitleLinks = makeArticleTitleLinks(ComparisonLink);

// Fill these out later at some point
type ArticleSummary = any;
type ComparisonSummary = any;

interface Props {
  guidesArticleSummaries: ArticleSummary[];
  reviewsArticleSummaries: ArticleSummary[];
  featuredArticleSummaries: ArticleSummary[];
  comparisonsSummaries: ComparisonSummary[];
  slideshows: SlideshowInterface[];
  heroBg: string;
  indexable: boolean;
}

export default function GuidesPage({
  guidesArticleSummaries,
  reviewsArticleSummaries,
  featuredArticleSummaries,
  comparisonsSummaries,
  slideshows,
  heroBg,
  indexable,
}: Props): JSX.Element {
  useContentGroups({ default: CONTENT_URL });
  const { t } = useTranslation();
  const adUnit = new CarGurusAdUnit({ zone: Zone.Research });
  const [underlinedTab, setUnderlinedTab] = useState('featured');
  const [shouldNavbarBeOpaque, setShouldNavbarBeOpaque] = useState(false);
  useWindowScroll();
  const navbarPosition = new NavbarPosition();
  // This is a hack so that the client-side build bundles this image
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const guidesImg = guidesAndReviewsHeroPng;

  function setNavbarUnderlinedTab(): void {
    if (!navbarPosition.currentSection()) {
      return;
    }

    setUnderlinedTab(navbarPosition.currentSection());
  }

  function updateNavbarState(): void {
    setNavbarUnderlinedTab();

    if (DeviceScreen.isAtLeast(SMALL.minWidth)) {
      setShouldNavbarBeOpaque(!navbarPosition.isNavbarInHero());
    }
  }

  useEffect(() => {
    updateNavbarState();
  });

  useAdSetup(adUnit.toString(), {
    make: null,
    model: null,
    year: null,
    pageType: PageType.Reviews,
    ageType: AgeType.None,
    cpo: false,
    inLine: 1,
    body_style: null,
    category: null,
    app: 'web',
    sellerId: null,
  });

  function scrollToSection(section: string): void {
    window.scrollTo(0, navbarPosition.scrollPositionForSection(section));
  }

  const featuredSummaries = ArticleSummariesPresenter.viewModelFrom(featuredArticleSummaries);
  const guidesSummaries = ArticleSummariesPresenter.viewModelFrom(
    guidesArticleSummaries.slice(0, 6)
  );
  const otherGuidesSummaries = ArticleSummariesPresenter.viewModelFrom(
    guidesArticleSummaries.slice(6)
  );
  const reviewsSummaries = ArticleSummariesPresenter.viewModelFrom(
    reviewsArticleSummaries.slice(0, 6)
  );
  const otherReviewsSummaries = ArticleSummariesPresenter.viewModelFrom(
    reviewsArticleSummaries.slice(6)
  );
  const slideshowSummaries = ArticleSummariesPresenter.viewModelFrom(slideshows.slice(0, 6));
  const slicedComparisonsSummaries = comparisonsSummaries.slice(0, 30);

  const featuredTitle = t('guides.featured');
  const guidesTitle = t('guides.guides');
  const reviewsTitle = t('guides.reviews');
  const comparisonsTitle = t('guides.comparisons');
  const moreTitle = t('guides.more');
  const slideshowsTitle = t('guides.photoGalleries');

  return (
    <>
      <Layout
        title={t('guides.meta.pageTitle')}
        description={t('guides.meta.metaDescription')}
        indexed={indexable}
      >
        <Head>
          <meta content={openGraphImage} property="og:image" key="og:image" />
          <meta content="article" property="og:type" key="og:type" />
        </Head>
        <div ref={navbarPosition.refs.hero} className="hero">
          <div className="container">
            <div className="hero-header">{t('guides.heroHeader')}</div>
          </div>
        </div>
        <GuidesNavbar
          ref={navbarPosition.refs.navbar}
          underlinedTab={underlinedTab}
          navbarOpacity={shouldNavbarBeOpaque}
          scrollToRef={scrollToSection}
          featuredTitle={featuredTitle}
          guidesTitle={guidesTitle}
          reviewsTitle={reviewsTitle}
          slideshowsTitle={slideshowsTitle}
        />
        <div className="container">
          <h1 ref={navbarPosition.refs.featured}>{featuredTitle}</h1>
          <div className="section-header-border" />
          <FeaturedArticleSummaries articleSummaries={featuredSummaries} />
          <h1 ref={navbarPosition.refs.guides}>{guidesTitle}</h1>
          <div className="section-header-border" />
          <ArticleSummaries
            engagementType={'guides_article_click'}
            onClick={trackArticleClick}
            articleSummaries={guidesSummaries}
          />
          <h1 ref={navbarPosition.refs.reviews}>{reviewsTitle}</h1>
          <div className="section-header-border" />
          <ArticleSummaries
            engagementType={'reviews_article_click'}
            onClick={trackArticleClick}
            articleSummaries={reviewsSummaries}
          />
          <h1 ref={navbarPosition.refs.slideshows}>{slideshowsTitle}</h1>
          <div className="section-header-border" />
          <ArticleSummaries
            engagementType={'slideshow_article_click'}
            onClick={trackArticleClick}
            articleSummaries={slideshowSummaries}
          />
          <h2>{`${moreTitle} ${guidesTitle}`}</h2>
          <div className="section-header-border" />
          <ArticlesTitleLinks
            engagementType={'guides_article_link_click'}
            onClick={trackArticleClick}
            articles={otherGuidesSummaries}
          />
          <h2>{`${moreTitle} ${reviewsTitle}`}</h2>
          <div className="section-header-border" />
          <ArticlesTitleLinks
            engagementType={'reviews_article_link_click'}
            onClick={trackArticleClick}
            articles={otherReviewsSummaries}
          />
          <h2>{`${moreTitle} ${comparisonsTitle}`}</h2>
          <div className="section-header-border" />
          <ComparisonTitleLinks
            engagementType={'comparisons_article_link_click'}
            onClick={trackArticleClick}
            articles={slicedComparisonsSummaries}
          />
        </div>
        <style jsx>
          {`
            .hero {
              height: 70px;
              background-size: cover;
              background-image: none;
              background-color: white;
              position: relative;
              width: 100vw;
              align-self: center;
            }

            .hero-header {
              margin-top: -20px;
              font-size: 20px;
              line-height: 1.1;
              font-weight: 400;
              color: ${colors.BLACK};
              position: absolute;
              top: 50%;
            }

            h1,
            h2 {
              font-size: 18px;
              font-weight: 600;
              line-height: 1.4;
              letter-spacing: normal;
              color: ${colors.BLACK};
              margin-bottom: 8px;
            }

            .section-header-border {
              height: 1px;
              width: 100%;
              background-color: ${colors.G2};
              position: relative;
              margin-bottom: 24px;
            }

            @media screen and (min-width: ${screen.smMin}) and (max-width: ${screen.smMax}) {
              .hero {
                height: 286px;
                background-image: url(${heroBg});
              }

              .hero-header {
                font-size: 36px;
                line-height: 0.93;
                color: white;
                margin-top: -20px;
              }

              h1 {
                font-size: 20px;
                line-height: 1.5;
              }
            }

            @media screen and (min-width: ${screen.mdMin}) {
              .hero {
                height: 380px;
                background-image: url(${heroBg});
              }

              .hero-header {
                font-size: 43px;
                line-height: 0.93;
                color: white;
              }

              h1,
              h2 {
                font-size: 28px;
                line-height: 1.07;
              }
            }
          `}
        </style>
      </Layout>
      <StructuredDataComponent
        data={breadcrumbStructuredData({
          breadcrumbCategory: 'guides',
        })}
      />
    </>
  );
}

function logErrorIfOutcomeRejected(outcome: PromiseSettledResult<any>): void {
  if (outcome.status === 'rejected') {
    captureException(outcome.reason);
    console.error(outcome.reason);
  }
}

// Ideally this would infer the PromiseSettledResult<resolvedValue> type
// and say it returns that, but I'm not sure how to do that
function extractValueFromOutcome(outcome: PromiseSettledResult<unknown>): any {
  if ((outcome as PromiseFulfilledResult<unknown>).value) {
    return (outcome as PromiseFulfilledResult<unknown>).value;
  }

  return undefined;
}

export const getServerSideProps: GetServerSideProps<Props> = async ({
  locale = i18n.language,
  req,
  res,
}) => {
  // Pages with article content are automatically invalidated from the web cache when
  // an article on those pages is published.  Therefore, we use the origin url here to
  // avoid caching old article data when the page is re-cached after invalidation.
  const bypassCache = true;

  const fetchFeatured = async (): Promise<ArticleSummary[]> => {
    return (await fetchGuidesFeaturedArticles({ locale, abridged: true }, { bypassCache }))
      .articles;
  };
  const fetchGuides = async (): Promise<ArticleSummary[]> => {
    return (
      await fetchArticles({ parentCategory: 'Guides', locale, abridged: true }, { bypassCache })
    ).articles;
  };
  const fetchReviews = async (): Promise<ArticleSummary[]> => {
    return (
      await fetchArticles({ parentCategory: 'Reviews', locale, abridged: true }, { bypassCache })
    ).articles;
  };
  const fetchComparisons = async (): Promise<ComparisonSummary[]> => {
    return (await fetchAllComparisons({ locale, abridged: true }, bypassCache)).comparisons;
  };
  const fetchSlideshows = async (): Promise<SlideshowInterface[]> => {
    return (await fetchAllSlideshows(bypassCache)).slideshows;
  };

  const [featuredOutcome, guidesOutcome, reviewsOutcome, comparisonsOutcome, slideshowsOutcome] =
    await Promise.allSettled([
      fetchFeatured(),
      fetchGuides(),
      fetchReviews(),
      fetchComparisons(),
      fetchSlideshows(),
    ]);

  [featuredOutcome, guidesOutcome, reviewsOutcome, comparisonsOutcome, slideshowsOutcome].forEach(
    (outcome) => {
      logErrorIfOutcomeRejected(outcome);
    }
  );

  enableContentCaching(res);

  const indexable = isIndexable(req);
  return {
    props: {
      featuredArticleSummaries: extractValueFromOutcome(featuredOutcome) ?? [],
      guidesArticleSummaries: extractValueFromOutcome(guidesOutcome) ?? [],
      reviewsArticleSummaries: extractValueFromOutcome(reviewsOutcome) ?? [],
      comparisonsSummaries: extractValueFromOutcome(comparisonsOutcome) ?? [],
      slideshows: extractValueFromOutcome(slideshowsOutcome) ?? [],
      heroBg:
        'https://autolist-web-assets-staging.s3.amazonaws.com/_next/public/static/images/guides-and-reviews-hero-6ce2ef3ff639afaaedb8158f2a99c6d2.png',
      indexable,
    },
  };
};
